/**
 * Adds transient ( `$`-prefixed ) versions of a component's propTypes.
 *
 * Transient props are props used for styling that should not be forwarded
 * to the DOM. By generating `$`-prefixed equivalents of each propType,
 * this helper allows components to accept both regular and transient
 * variants of the same prop.
 *
 * Useful when you want prop-type validation for transient styling props,
 * especially in styled-components or similar style systems.
 *
 * @example
 * const propTypes = { color: PropTypes.string };
 *
 * addTransientPropTypes(propTypes);
 * // Returns:
 * // {
 * //   color: PropTypes.string,
 * //   $color: PropTypes.string
 * // }
 *
 * @param propTypes - A mapping of prop names to their prop-type validators.
 * @returns A new object containing the original propTypes along with
 *          `$`-prefixed transient versions.
 */
export const addTransientPropTypes = (
  propTypes: Record<string, unknown> = {},
) => {
  const transientPropTypes = Object.fromEntries(
    Object.entries(propTypes).map(([key, value]) => [`$${key}`, value]),
  );
  return { ...propTypes, ...transientPropTypes };
};
